// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import SignInButton from "@adl/App/Components/Common/SignInButton";
import ProductActions, { ProductActionsStyle } from "@adl/App/Components/Product/ProductActions";
import ProductPreviousPurchase from "@adl/App/Components/Product/ProductPreviousPurchase";
import productDescriptionStyles from "@adl/App/SharedStyle/ProductDescriptionTextStyles";
import ProductAddToListLink, { ProductAddToListLinkStyles } from "@adl/Overrides/Components/ProductAddToListLink";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@adl/Overrides/Components/ProductPartNumbers";
import { color, mergeWithCss, xIsAuthenticated, xScreen } from "@adl/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
    // XNG-Changes
    query?: string;
    doSearch?: () => void;
    products: ProductContextModel[];
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        isAuthenticated: xIsAuthenticated(state),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

interface AutoCompleteProductStyles {
    productWrapper?: InjectableCss;
    productContainer?: InjectableCss;
    productInfoContainer?: InjectableCss;
    productImage?: ProductImageStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    actionsContainer?: InjectableCss;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productQuantityOrderedStyles?: TextFieldProps;
    signInButton?: ButtonPresentationProps;
    addToListLink?: ProductAddToListLinkStyles;
    productActionsStyles?: ProductActionsStyle;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    // XNG-Changes
    titleWrapper?: InjectableCss;
    viewAllButton?: ButtonPresentationProps;
    productStyles?: AutoCompleteProductStyles;
    productPreviousPurchase?: TypographyPresentationProps;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        size: 16,
        weight: "bold",
        ellipsis: true,
        css: css`
            text-align: left;
            letter-spacing: 0;
            line-height: 17px;
            margin-right: 10px;
            & > ${TypographyStyle} {
                display: block;
                font-size: 20px;
                font-weight: bold;
                letter-spacing: 0;
                line-height: 28px;
            }
        `,
    },
    container: {
        css: css`
            flex-grow: 1;
            display: flex;
        `,
    },

    image: {
        width: "72px",
        height: "72px",
        css: css`
            margin-right: 16px;
            flex-shrink: 0;

            img {
                height: auto;
                max-height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
            `,
        },
        css: css`
            width: 270px;
        `,
    },
    erpNumberText: {
        ellipsis: true,
        css: css`
            width: 270px;
            text-align: left;
            margin: 5px 0 0 0;
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            flex-wrap: wrap;
            padding-bottom: 16px;
            margin-bottom: 4px;
            border-bottom: 1px solid ${color("common.ui5")};
        `,
    },
    viewAllButton: {
        variant: "secondary",
        css: css`
            width: 127px;
        `,
        typographyProps: {
            size: 12,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 13px;
            `,
        },
    },

    productStyles: {
        productWrapper: {
            css: css`
                display: flex;
                padding: 12px 0 8px;
                flex-wrap: nowrap;
                ${xScreen("lte", "sm")} {
                    flex-direction: column;
                }

                &:not(.last-item) {
                    margin-bottom: 12px;
                }
            `,
        },
        productDescriptionStyles: {
            ...productDescriptionStyles("small", {
                productDetailLink: {
                    css: css`
                        width: auto;
                        margin-bottom: 6px;
                        line-height: 17px;
                    `,
                    typographyProps: {
                        css: css`
                            ${wrapInContainerStyles};
                            line-height: 17px;
                        `,
                    },
                },
            }),
        },
        productContainer: {
            css: css`
                flex-grow: 1;
                display: flex;
                ${xScreen("lte", "xs")} {
                    flex-wrap: wrap;
                }
            `,
        },
        productInfoContainer: {
            css: css`
                flex-grow: 1;
                display: flex;
                flex-direction: column;
                margin-right: 20px;
                ${xScreen("lte", "xs")} {
                    margin-bottom: 20px;
                }
            `,
        },
        actionsContainer: {
            css: css`
                flex-grow: 0;
                flex-shrink: 0;
                flex-basis: 138px;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;

                &.sign-in {
                    flex-basis: 185px;
                }

                ${xScreen("lte", "xs")} {
                    flex-basis: auto;
                }
            `,
        },
        productImage: {
            linkWrappingImage: {
                css: css`
                    height: 88px;
                    width: 80px;
                    margin-right: 12px;
                    flex-shrink: 0;
                    ${xScreen("lte", "sm")} {
                        margin: 0 0 20px;
                    }
                `,
            },
            img: {
                width: "80px",
                height: "88px",
                css: css`
                    height: auto;
                    width: 100%;
                    max-height: 100%;
                    overflow: hidden;
                `,
            },
        },
        productQuantityOrderedStyles: {
            sizeVariant: "small",
            cssOverrides: {
                formField: css`
                    width: 45px;
                `,
                inputSelect: css`
                    padding: 4px;
                    text-align: center;
                    width: 100%;
                `,
            },
        },
        productPartNumbersStyles: {
            container: {
                css: css`
                    margin-bottom: 7px;
                `,
            },
        },
        addToListLink: {
            link: {
                css: css`
                    margin-bottom: 20px;
                `,
            },
            button: {
                sizeVariant: "small",
                css: css`
                    margin-bottom: 20px;
                `,
            },
        },
        signInButton: {
            css: css`
                margin-bottom: 20px;
            `,
        },
        productActionsStyles: {
            productAddToCartButtonStyles: {
                variant: "secondary",
                sizeVariant: "small",
            },

            productQuantityOrderedStyles: {
                actionsStyles: {
                    quantityOrderedTextField: {
                        sizeVariant: "small",
                    },
                },
            },
            addToCartContainer: {
                css: css`
                    justify-content: flex-start;
                    margin-bottom: 20px;
                    > div[class^="GridOffset-sc"] {
                        align-items: center;
                    }
                `,
            },
            qtyWrapper: {
                css: css`
                    margin-right: 8px;
                    ${xScreen("lte", "xs")} {
                        margin-left: 0;
                        margin-right: 8px;
                    }
                `,
            },
            addToCartButtonWrapper: {
                css: css`
                    flex-grow: 0;
                    > button {
                        padding: 0;
                        border-width: 1.5px;
                    }
                `,
            },
            // qtyGridItem: {
            //     width: 6,
            // },
            // addToCartButtonGridItem: {
            //     width: 4,
            // },
        },
    },
    productPreviousPurchase: {
        css: css`
            width: 100%;
        `,
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products } = this.props;
        const styles = this.styles;
        return (
            <>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography
                        {...styles.headerText}
                        data-test-selector="autocompleteProducts_Header"
                        className="autocomplete-products-header"
                    >
                        {translate("Recommended Products for")}
                        {/* XNG-Changes: ADL-1190 - render searched query using dangerouslySetInnerHTML */}
                        <Typography
                            ellipsis={true}
                            transform="capitalize"
                            dangerouslySetInnerHTML={{ __html: this.props.query ? this.props.query : "" }}
                            data-test-selector="autocompleteProducts_Header"
                        />
                    </Typography>
                    <Button
                        {...styles.viewAllButton}
                        onClick={this.props.doSearch}
                        data-test-selector="search-view-all"
                    >
                        {translate("Shop All")}
                    </Button>
                </StyledWrapper>
                {products.map((productContext, index) => {
                    return (
                        <ProductContext.Provider key={productContext.product.id} value={productContext}>
                            <StyledWrapper
                                {...styles.productStyles?.productWrapper}
                                className={`${index === products.length - 1 ? "last-item" : ""}`}
                            >
                                <ProductImage
                                    product={productContext}
                                    extendedStyles={styles.productStyles?.productImage}
                                />
                                <StyledWrapper {...styles.productStyles?.productContainer}>
                                    <StyledWrapper {...styles.productStyles?.productInfoContainer}>
                                        <StyledWrapper>
                                            <ProductDescription
                                                product={productContext}
                                                extendedStyles={mergeWithCss(
                                                    styles.productStyles?.productDescriptionStyles,
                                                    {
                                                        productDetailLink: {
                                                            onClick: () => hideAll(),
                                                        },
                                                    },
                                                )}
                                            />
                                        </StyledWrapper>
                                        <ProductPartNumbers
                                            productNumber={productContext.product.productNumber}
                                            manufacturerItem={productContext.product.manufacturerItem}
                                            extendedStyles={styles.productStyles?.productPartNumbersStyles}
                                            brand={productContext.product?.brand}
                                        />
                                    </StyledWrapper>
                                    <StyledWrapper
                                        {...styles.productStyles?.actionsContainer}
                                        className={`${
                                            this.props.isAuthenticated && productContext.product.canAddToCart
                                                ? "add-to-cart"
                                                : "sign-in"
                                        }`}
                                    >
                                        {!this.props.isAuthenticated ? (
                                            <SignInButton extendedStyles={styles.productStyles?.signInButton} />
                                        ) : !productContext.product.canAddToCart ? (
                                            <ProductAddToListLink
                                                data-test-selector={`actionsAddToList${productContext.product.id}`}
                                                extendedStyles={styles.productStyles?.addToListLink}
                                            />
                                        ) : (
                                            <ProductActions
                                                iconOnly={true}
                                                extendedStyles={styles.productStyles?.productActionsStyles}
                                            />
                                        )}
                                        <ProductPreviousPurchase
                                            product={productContext.product}
                                            extendedStyles={styles.productPreviousPurchase}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        </ProductContext.Provider>
                    );
                })}
            </>
        );
    }
}

export default connect(mapStateToProps)(AutocompleteProducts);
